import * as React from 'react';
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	Link,
	Container,
	Alert,
	IconButton,
	InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from "react-router";
import EventsApi from '../../api/eventsApi/eventsApi';

export default function ForgotPassword() {
	const navigate = useNavigate();
	const [step, setStep] = React.useState<'email' | 'reset'>('email');
	const [showPassword, setShowPassword] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [success, setSuccess] = React.useState<string | null>(null);
	const [loading, setLoading] = React.useState(false);
	const [email, setEmail] = React.useState('');
	const [code, setCode] = React.useState('');

	const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setLoading(true);
		
		const data = new FormData(event.currentTarget);
		const emailValue = data.get('email') as string;
		setEmail(emailValue);

		try {
			await EventsApi.sendPasswordReset(emailValue);
			setLoading(false);
			setStep('reset');
			setSuccess('Şifre sıfırlama kodu e-posta adresinize gönderildi.');
		} catch (error: any) {
			setLoading(false);
			setError(error?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
		}
	};

	const handleResetSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setSuccess(null);
		setLoading(true);
		
		const data = new FormData(event.currentTarget);
		const resetData = {
			email: email,
			code: code,
			newPassword: data.get('newPassword') as string,
		};

		try {
			await EventsApi.resetPassword(resetData);
			setLoading(false);
			setSuccess('Şifreniz başarıyla değiştirildi. Giriş sayfasına yönlendiriliyorsunuz...');
			
			// Redirect to login after 1 second
			setTimeout(() => {
				navigate('/login');
			}, 1000);
		} catch (error: any) {
			setLoading(false);
			setError(error?.message || 'Şifre sıfırlama sırasında bir hata oluştu. Lütfen tekrar deneyin.');
		}
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value.replace(/\D/g, '').slice(0, 6);
		setCode(value);
	};

	return (
		<Box
			sx={{
				minHeight: '100vh',
				backgroundColor: '#ffffff',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: 2,
			}}
		>
			<Container maxWidth="xs">
				<Card
					elevation={3}
					sx={{
						borderRadius: 3,
						backgroundColor: '#ffffff',
						border: '1px solid #e0e0e0',
					}}
				>
					<CardContent sx={{ p: 4 }}>
						{step === 'email' ? (
							<Box component="form" onSubmit={handleEmailSubmit} noValidate>
								<Typography
									variant="h5"
									component="h2"
									gutterBottom
									sx={{
										fontWeight: 600,
										color: '#094151',
										mb: 3,
									}}
								>
									Şifremi Unuttum
								</Typography>

								<Typography
									variant="body2"
									sx={{
										color: '#666',
										mb: 3,
									}}
								>
									E-posta adresinizi girin, size şifre sıfırlama kodu gönderelim.
								</Typography>

								{error && (
									<Alert severity="error" sx={{ mb: 3 }}>
										{error}
									</Alert>
								)}

								{success && (
									<Alert severity="success" sx={{ mb: 3 }}>
										{success}
									</Alert>
								)}

								<Box mb={4}>
									<Typography
										variant="subtitle2"
										sx={{ mb: 1, fontWeight: 600, color: '#333' }}
									>
										E-posta
									</Typography>
									<TextField
										required
										fullWidth
										id="email"
										name="email"
										type="email"
										placeholder="E-posta adresinizi girin"
										variant="outlined"
										sx={{
											'& .MuiOutlinedInput-root': {
												borderRadius: 2,
												backgroundColor: '#f8f9ff',
												'&:hover fieldset': {
													borderColor: '#094151',
												},
												'&.Mui-focused fieldset': {
													borderColor: '#094151',
												},
											},
										}}
									/>
								</Box>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									disabled={loading}
									sx={{
										mt: 2,
										mb: 4,
										py: 1.5,
										backgroundColor: '#094151',
										color: '#fff',
										fontWeight: 600,
										borderRadius: '8px',
										boxShadow: 'none',
										'&:hover': {
											backgroundColor: '#022631',
											color: '#fff',
										},
										'&:disabled': {
											backgroundColor: '#ccc',
										},
									}}
								>
									{loading ? 'Kod gönderiliyor...' : 'Şifremi Değiştir'}
								</Button>

								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
								>
									<Typography variant="body2" sx={{ color: '#666', mr: 1 }}>
										Hatırladınız mı?
									</Typography>
									<Link
										href="#"
										variant="body2"
										sx={{
											color: '#094151',
											textDecoration: 'none',
											fontWeight: 500,
											'&:hover': {
												textDecoration: 'underline',
											},
										}}
										onClick={(e) => {
											e.preventDefault();
											navigate('/login');
										}}
									>
										Giriş Yap
									</Link>
								</Box>
							</Box>
						) : (
							<Box component="form" onSubmit={handleResetSubmit} noValidate>
								<Typography
									variant="h5"
									component="h2"
									gutterBottom
									sx={{
										fontWeight: 600,
										color: '#094151',
										mb: 3,
									}}
								>
									Şifre Sıfırla
								</Typography>

								<Typography
									variant="body2"
									sx={{
										color: '#666',
										mb: 3,
									}}
								>
									{email} adresine gönderilen kodu girin ve yeni şifrenizi belirleyin.
								</Typography>

								{error && (
									<Alert severity="error" sx={{ mb: 3 }}>
										{error}
									</Alert>
								)}

								{success && (
									<Alert severity="success" sx={{ mb: 3 }}>
										{success}
									</Alert>
								)}

								<Box mb={3}>
									<Typography
										variant="subtitle2"
										sx={{ mb: 1, fontWeight: 600, color: '#333' }}
									>
										E-posta Kodu
									</Typography>
									<TextField
										required
										fullWidth
										id="code"
										name="code"
										value={code}
										onChange={handleCodeChange}
										placeholder="6 haneli kod"
										variant="outlined"
										inputProps={{
											maxLength: 6,
											style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
										}}
										sx={{
											'& .MuiOutlinedInput-root': {
												borderRadius: 2,
												backgroundColor: '#f8f9ff',
												'&:hover fieldset': {
													borderColor: '#094151',
												},
												'&.Mui-focused fieldset': {
													borderColor: '#094151',
												},
											},
										}}
									/>
								</Box>

								<Box mb={4}>
									<Typography
										variant="subtitle2"
										sx={{ mb: 1, fontWeight: 600, color: '#333' }}
									>
										Yeni Şifre
									</Typography>
									<TextField
										required
										fullWidth
										name="newPassword"
										type={showPassword ? 'text' : 'password'}
										id="newPassword"
										placeholder="••••••••••••"
										variant="outlined"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={handleClickShowPassword}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											),
										}}
										sx={{
											'& .MuiOutlinedInput-root': {
												borderRadius: 2,
												backgroundColor: '#f8f9ff',
												'&:hover fieldset': {
													borderColor: '#094151',
												},
												'&.Mui-focused fieldset': {
													borderColor: '#094151',
												},
											},
										}}
									/>
								</Box>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									disabled={loading || code.length !== 6}
									sx={{
										mt: 2,
										mb: 4,
										py: 1.5,
										backgroundColor: '#094151',
										color: '#fff',
										fontWeight: 600,
										borderRadius: '8px',
										boxShadow: 'none',
										'&:hover': {
											backgroundColor: '#022631',
											color: '#fff',
										},
										'&:disabled': {
											backgroundColor: '#ccc',
										},
									}}
								>
									{loading ? 'Şifre değiştiriliyor...' : 'Şifremi Belirle'}
								</Button>

								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
								>
									<Link
										href="#"
										variant="body2"
										sx={{
											color: '#094151',
											textDecoration: 'none',
											fontWeight: 500,
											'&:hover': {
												textDecoration: 'underline',
											},
										}}
										onClick={(e) => {
											e.preventDefault();
											setStep('email');
											setError(null);
											setSuccess(null);
										}}
									>
										← Geri Dön
									</Link>
								</Box>
							</Box>
						)}
					</CardContent>
				</Card>
				
				{/* Logo and Company Name */}
				<Box 
					sx={{ 
						display: 'flex', 
						alignItems: 'center', 
						justifyContent: 'center',
						gap: 1,
						mt: 3,
						opacity: 0.8
					}}
				>
					<img
						src="/logo_inttech.png"
						alt="Int-Tech Logo"
						style={{
							width: '20px',
							height: '20px',
							objectFit: 'contain',
						}}
					/>
					<Typography
						variant="body2"
						component="div"
						sx={{
							fontFamily: 'Conthrax, monospace',
							fontWeight: 600,
							letterSpacing: '.08rem',
							color: '#666',
							fontSize: '0.85rem',
						}}
					>
						INT-TECH
					</Typography>
				</Box>
			</Container>
		</Box>
	);
} 